import axios from '../../../utils/axios';
import { createSlice } from '@reduxjs/toolkit';
import { AppDispatch } from '../../store';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Contact } from '../../../app/locations/[bui]/ContactsTable';
import {
  getContacts as getContactsFromSupabase,
  addContact as addContactToSupabase,
  updateContact as updateContactInSupabase,
  deleteContact as deleteContactInSupabase
} from '../../../server/supabase/contacts';

const API_URL = '/api/data/contacts/ContactsData';

interface StateType {
  contacts: Contact[];
  contactContent: number;
  contactSearch: string;
  editContact: boolean;
  currentFilter: string;
}

const initialState: StateType = {
  contacts: [],
  contactContent: 1,
  contactSearch: '',
  editContact: false,
  currentFilter: 'show_all',
};

export const ContactSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    getContacts: (state: StateType, action: PayloadAction<Contact[]>) => {
      state.contacts = action.payload;
    },
    SearchContact: (state: StateType, action: PayloadAction<string>) => {
      state.contactSearch = action.payload;
    },
    SelectContact: (state: StateType, action: PayloadAction<number>) => {
      state.contactContent = action.payload;
    },
    DeleteContact: (state: StateType, action: PayloadAction<string>) => {
      state.contacts = state.contacts.map((contact) =>
        contact.id === action.payload ? { ...contact, deleted: !contact.deleted } : contact,
      );
    },
    toggleStarredContact: (state: StateType, action: PayloadAction<number>) => {
      state.contacts = state.contacts.map((contact) =>
        contact.id === action.payload ? { ...contact, starred: !contact.starred } : contact,
      );
    },
    isEdit: (state: StateType) => {
      state.editContact = !state.editContact;
    },
    setVisibilityFilter: (state: StateType, action: PayloadAction<string>) => {
      state.currentFilter = action.payload;
    },
    UpdateContact: {
      reducer(state: StateType, action: PayloadAction<Contact>) {
        state.contacts = state.contacts.map((contact) =>
          contact.id === action.payload.id ? action.payload : contact
        );
      },
      prepare(contact: Contact) {
        return { payload: contact };
      },
    },
    addContactAction: {
      reducer(state: StateType, action: PayloadAction<Contact>) {
        state.contacts.push(action.payload);
      },
      prepare(contact: Contact) {
        return { payload: contact };
      },
    },
  },
});

export const {
  getContacts,
  SearchContact,
  isEdit,
  SelectContact,
  DeleteContact,
  toggleStarredContact,
  UpdateContact,
  addContactAction,
  setVisibilityFilter,
} = ContactSlice.actions;

export const fetchContacts = () => async (dispatch: AppDispatch) => {
  try {
    console.log('Fetching contacts from Supabase...');
    const data = await getContactsFromSupabase();
    console.log('Supabase Response:', data);
    dispatch(getContacts(data));
  } catch (error) {
    console.error('Error fetching contacts:', error);
  }
};

export const addContact = (contact: Omit<Contact, 'id'>) => async (dispatch: AppDispatch) => {
  try {
    const data = await addContactToSupabase(contact);
    dispatch(addContactAction(data));
  } catch (error) {
    console.error('Error adding contact:', error);
    throw error;
  }
};

export const updateContact = (contact: Contact) => async (dispatch: AppDispatch) => {
  try {
    const updatedContact = await updateContactInSupabase(contact);
    dispatch(UpdateContact(updatedContact));
  } catch (error) {
    console.error('Error updating contact:', error);
    throw error;
  }
};

export const deleteContact = (id: string) => async (dispatch: AppDispatch) => {
  try {
    await deleteContactInSupabase(id);
    dispatch(DeleteContact(id));
  } catch (error) {
    console.error('Error deleting contact:', error);
    throw error;
  }
};

export default ContactSlice.reducer;
